import { useContext, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import yup from 'utils/yup';
import { diContainerContext } from 'application/contexts/useDiContainer';
import { SupporterBuyerRegistrationProfileInfoQuery } from 'interfaceAdapter/queries/SupporterBuyerRegistrationProfileInfo';
import { SupporterCompanyDetailQuery } from 'interfaceAdapter/queries/SupporterCompanyDetail';
import { SupporterBuyerEditRepository } from 'interfaceAdapter/repositories/SupporterBuyerEdit';
import { useLoading } from 'application/contexts/useLoading';
import { CompanySearchResult } from 'domain/entities/CompanySearchResult/CompanySearchResult';
import { IAreaOption } from 'domain/entities/Buyer/Buyer';

const schema = yup
    .object({
        name: yup.string().required(),
        company_code: yup
            .string()
            .nullable()
            .matches(/^[0-9]+$/, '半角数字のみ入力可能です')
            .transform((value) => value || null)
            .default(null),
        target_area: yup.array().of(yup.string()).required(),
        target_company_industries: yup.array().of(yup.string()).required(),
        target_company_employee_size_lower: yup.string().nullable(),
        target_company_employee_size_upper: yup.string().nullable(),
        target_company_industry_details: yup.string().nullable().max(2000),
        target_company_purchase_amount_lower: yup.string().nullable(),
        target_company_purchase_amount_upper: yup.string().nullable(),
        target_company_revenue_scale_lower: yup.string().nullable(),
        target_company_revenue_scale_upper: yup.string().nullable(),
        target_company_profit_level_lower: yup.string().nullable(),
        target_company_profit_level_upper: yup.string().nullable(),
        target_company_net_assets_level_lower: yup.string().nullable(),
        target_company_net_assets_level_upper: yup.string().nullable(),
        has_acquisition_experience: yup.string().nullable(),
        other_buying_points: yup.string().nullable().max(1000),
        number_of_employees: yup.string(),
        revenue_scale: yup.string(),
        business_description: yup.string().max(1000),
        question: yup.string().nullable().max(1000),
        icon_image: yup.string().nullable(),
        hp_url: yup.string().nullable(),
        fee_structure: yup.boolean().required(),
        other_buyers: yup.boolean().required(),
        contract_status: yup.boolean().required(),
        process_flow: yup.boolean().required(),
        business_synergy: yup.boolean().required(),
        support_system: yup.boolean().required(),
        fee_structure_txt: yup.string().default('').max(1000),
        other_buyers_txt: yup.string().default('').max(1000),
        contract_status_txt: yup.string().default('').max(1000),
        process_flow_txt: yup.string().default('').max(1000),
        business_synergy_txt: yup.string().default('').max(1000),
        support_system_txt: yup.string().default('').max(1000),
        is_sole_proprietor: yup.boolean().required(),
    })
    .required();

type BuyerForm = yup.InferType<typeof schema>;

export const useSupporterBuyerEdit = () => {
    const diContainer = useContext(diContainerContext);
    const supporterBuyerRegistrationProfileInfoQuery = diContainer.resolve(SupporterBuyerRegistrationProfileInfoQuery);
    const supporterCompanyDetailQuery = diContainer.resolve(SupporterCompanyDetailQuery);
    const supporterBuyerEditRepository = diContainer.resolve(SupporterBuyerEditRepository);
    const { loading, setLoading } = useLoading();
    const navigate = useNavigate();
    const params = useParams<{ buyerId: string; type: 'seller' | 'buyer' }>();

    const [buyerData, setBuyerData] = useState<any>(null);
    const [areaChoices, setAreaChoices] = useState<IAreaOption[]>([]);
    const [employeeChoices, setEmployeeChoices] = useState<string[][]>([]);
    const [revenueChoices, setRevenueChoices] = useState<string[][]>([]);
    const [industryChoices, setIndustryChoices] = useState<string[][]>([]);
    const [purchaseAmountLowerChoices, setPurchaseAmountLowerChoices] = useState<string[][]>([]);
    const [purchaseAmountUpperChoices, setPurchaseAmountUpperChoices] = useState<string[][]>([]);
    const [revenueScaleLowerChoices, setRevenueScaleLowerChoices] = useState<string[][]>([]);
    const [revenueScaleUpperChoices, setRevenueScaleUpperChoices] = useState<string[][]>([]);
    const [profitLevelLowerChoices, setProfitLevelLowerChoices] = useState<string[][]>([]);
    const [profitLevelUpperChoices, setProfitLevelUpperChoices] = useState<string[][]>([]);
    const [employeeLowerChoices, setEmployeeLowerChoices] = useState<string[][]>([]);
    const [employeeUpperChoices, setEmployeeUpperChoices] = useState<string[][]>([]);
    const [netAssetsLevelLowerChoices, setNetAssetsLevelLowerChoices] = useState<string[][]>([]);
    const [netAssetsLevelUpperChoices, setNetAssetsLevelUpperChoices] = useState<string[][]>([]);
    const [hasAcquisitionExperienceChoices, setHasAcquisitionExperienceChoices] = useState<string[][]>([]);
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackBarMessage, setSnackbarMessage] = useState('');
    const [editing, setEditing] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const {
        control,
        handleSubmit,
        formState: { errors },
        watch,
        setValue,
    } = useForm<BuyerForm>({
        resolver: yupResolver(schema),
        mode: 'onChange',
        defaultValues: {
            name: '',
        },
    });

    // 買い手企業詳細情報を取得
    useEffect(() => {
        const fetchBuyerData = async () => {
            if (!params.buyerId || params.type !== 'buyer') {
                setError('買い手企業のみ編集可能です');
                return;
            }

            setLoading(true);
            try {
                const data = await supporterCompanyDetailQuery.fetchBuyerDetail(Number(params.buyerId));
                setBuyerData(data);

                // フォームにデータを設定
                const area = Array.isArray(data.area) ? data.area : (data.area as string).split(',');
                setValue('name', data.name || '');
                setValue('company_code', data.company_code || null);
                setValue('icon_image', data.icon_image || null);
                setValue('target_area', area);
                setValue('target_company_industries', data.target_company_industries || []);
                setValue('target_company_employee_size_lower', data.target_company_employee_size_lower || null);
                setValue('target_company_employee_size_upper', data.target_company_employee_size_upper || null);
                setValue('target_company_revenue_scale_lower', data.target_company_revenue_scale_lower || null);
                setValue('target_company_revenue_scale_upper', data.target_company_revenue_scale_upper || null);
                setValue('target_company_profit_level_lower', data.target_company_profit_level_lower || null);
                setValue('target_company_profit_level_upper', data.target_company_profit_level_upper || null);
                setValue('target_company_net_assets_level_lower', data.target_company_net_assets_level_lower || null);
                setValue('target_company_net_assets_level_upper', data.target_company_net_assets_level_upper || null);
                setValue('has_acquisition_experience', data.has_acquisition_experience || null);
                setValue('target_company_industry_details', data.target_company_industry_details || null);
                setValue('target_company_purchase_amount_lower', data.target_company_purchase_amount_lower || null);
                setValue('target_company_purchase_amount_upper', data.target_company_purchase_amount_upper || null);
                setValue('other_buying_points', data.other_buying_points || null);
                setValue('number_of_employees', data.number_of_employees || '');
                setValue('revenue_scale', data.revenue_scale !== '99' ? data.revenue_scale || '' : '');
                setValue('fee_structure', !!data.fee_structure);
                setValue('other_buyers', !!data.other_buyers);
                setValue('contract_status', !!data.contract_status);
                setValue('process_flow', !!data.process_flow);
                setValue('business_synergy', !!data.business_synergy);
                setValue('support_system', !!data.support_system);
                setValue('fee_structure_txt', data.fee_structure_txt || '');
                setValue('other_buyers_txt', data.other_buyers_txt || '');
                setValue('contract_status_txt', data.contract_status_txt || '');
                setValue('process_flow_txt', data.process_flow_txt || '');
                setValue('business_synergy_txt', data.business_synergy_txt || '');
                setValue('support_system_txt', data.support_system_txt || '');
                setValue('business_description', data.business_description || '');
                setValue('question', data.question || null);
                setValue('hp_url', data.hp_url || null);
                setValue('is_sole_proprietor', !!data.is_sole_proprietor);
            } catch (err) {
                console.error('買い手企業情報の取得に失敗しました:', err);
                setError(err instanceof Error ? err.message : '買い手企業情報の取得に失敗しました');
            } finally {
                setLoading(false);
            }
        };

        fetchBuyerData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.buyerId, params.type]);

    // プロフィール情報の選択肢を取得
    useEffect(() => {
        const fetchProfileInfo = async () => {
            try {
                const data = await supporterBuyerRegistrationProfileInfoQuery.fetch();
                setAreaChoices(data.area_choices);
                setEmployeeChoices(data.employee_choices);
                setRevenueChoices(data.revenue_choices);
                setIndustryChoices(data.industry_choices);
                setPurchaseAmountLowerChoices(data.purchase_amount_lower_choices);
                setPurchaseAmountUpperChoices(data.purchase_amount_upper_choices);
                setRevenueScaleLowerChoices(data.revenue_scale_lower_choices);
                setRevenueScaleUpperChoices(data.revenue_scale_upper_choices);
                setProfitLevelLowerChoices(data.profit_level_lower_choices);
                setProfitLevelUpperChoices(data.profit_level_upper_choices);
                setEmployeeLowerChoices(data.employee_lower_choices);
                setEmployeeUpperChoices(data.employee_upper_choices);
                setNetAssetsLevelLowerChoices(data.net_assets_level_lower_choices);
                setNetAssetsLevelUpperChoices(data.net_assets_level_upper_choices);
                setHasAcquisitionExperienceChoices(data.has_acquisition_experience_choices);
            } catch (err) {
                console.error('プロフィール情報の取得に失敗しました:', err);
            }
        };
        fetchProfileInfo();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const submit = handleSubmit(async (data) => {
        if (!params.buyerId) {
            setSnackbarMessage('買い手企業IDが指定されていません。');
            setShowSnackbar(true);
            return;
        }

        setLoading(true);
        try {
            await supporterBuyerEditRepository.editProfile({
                buyer_id: Number(params.buyerId),
                ...data,
            });
            setSnackbarMessage('買い手企業プロフィールを更新しました。');
            setShowSnackbar(true);
            setTimeout(() => navigate('/supporter/dashboard/'), 1500);
        } catch (err) {
            setSnackbarMessage(err instanceof Error ? err.message : '買い手企業プロフィールの更新に失敗しました。');
            setShowSnackbar(true);
        } finally {
            setLoading(false);
        }
    });

    const onCompanySelect = (company: CompanySearchResult) => {
        setValue('name', company.name);
        setValue('company_code', company.number);
    };

    // userオブジェクトを模擬（BuyerEditFormで必要）
    const user = buyerData
        ? {
              buyer: buyerData,
          }
        : null;

    return {
        control,
        errors,
        loading,
        showSnackbar,
        areaChoices,
        employeeChoices,
        revenueChoices,
        industryChoices,
        purchaseAmountLowerChoices,
        purchaseAmountUpperChoices,
        revenueScaleLowerChoices,
        revenueScaleUpperChoices,
        profitLevelLowerChoices,
        profitLevelUpperChoices,
        employeeLowerChoices,
        employeeUpperChoices,
        netAssetsLevelLowerChoices,
        netAssetsLevelUpperChoices,
        hasAcquisitionExperienceChoices,
        editing,
        user,
        snackBarMessage,
        setEditing,
        setShowSnackbar,
        submit,
        setValue,
        watch,
        onCompanySelect,
        error,
        buyerType: params.type,
    };
};
